import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Users, CheckCircle, Briefcase, Hourglass, Banknote, Wallet, Zap, Package, Home, ArrowRight } from 'lucide-react';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/users/admin/stats').then(r => setStats(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  if (loading) return <DashboardLayout><div className="loader-wrap"><div className="loader"></div></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <h1 className="page-title">Admin Dashboard</h1>
      <p className="page-subtitle">Platform overview and management</p>

      {/* Admin's own referral link — the root of the whole tree. Unlike
          regular users, this is always visible (not gated on an active
          package) since the admin account isn't expected to buy one. */}
      <div className="referral-box" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>Admin Referral Code</div>
            <div className="referral-code-display">{user?.referralCode}</div>
            <div style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>Anyone who signs up with this link joins directly under you (the root)</div>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 6 }}>Referral Link</div>
            <div className="copy-link">
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {window.location.origin}/register/{user?.referralCode}
              </span>
              <button className="copy-btn" onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/register/${user?.referralCode}`);
                alert('Copied!');
              }}>Copy</button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 28 }}>
        {[
          { icon: Users, label: 'Total Users', value: stats?.totalUsers || 0, color: '#3b82f6', link: '/admin/users' },
          { icon: CheckCircle, label: 'Active Users', value: stats?.activeUsers || 0, color: '#22c55e', link: '/admin/users' },
          { icon: Briefcase, label: 'Total Business', value: formatCurrency(stats?.totalBusiness), color: 'var(--primary)', link: '/admin/packages' },
          { icon: Hourglass, label: 'Pending Packages', value: stats?.pendingPackages || 0, color: '#f59e0b', link: '/admin/packages' },
          { icon: Banknote, label: 'Pending Withdrawals', value: stats?.pendingWithdrawals || 0, color: '#ef4444', link: '/admin/withdrawals' },
          { icon: Wallet, label: 'Total Withdrawn', value: formatCurrency(stats?.totalWithdrawn), color: '#a855f7', link: '/admin/withdrawals' },
        ].map(s => (
          <Link key={s.label} to={s.link} className="stat-card" style={{ textDecoration: 'none' }}>
            <div className="stat-icon" style={{ background: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}><s.icon size={22} /></div>
            <div className="stat-info">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Zap size={18} /> Quick Actions</h3></div>
        <div className="grid-4">
          {[
            { label: 'Manage Users', icon: Users, link: '/admin/users', color: '#3b82f6' },
            { label: 'Approve Packages', icon: Package, link: '/admin/packages', color: 'var(--primary)' },
            { label: 'Process Withdrawals', icon: Banknote, link: '/admin/withdrawals', color: '#22c55e' },
            { label: 'Back to Home', icon: Home, link: '/', color: 'var(--text-muted)' },
          ].map(a => (
            <Link key={a.label} to={a.link} className="info-box" style={{ textAlign: 'center', textDecoration: 'none' }}>
              <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: a.color }}><a.icon size={36} /></div>
              <div className="info-box-title" style={{ color: a.color }}>{a.label}</div>
            </Link>
          ))}
        </div>
      </div>

      {/* Pending Alerts */}
      {(stats?.pendingPackages > 0 || stats?.pendingWithdrawals > 0) && (
        <div>
          {stats?.pendingPackages > 0 && (
            <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Package size={16} /> <strong>{stats.pendingPackages}</strong> package{stats.pendingPackages !== 1 ? 's' : ''} awaiting approval.
              <Link to="/admin/packages" style={{ color: 'var(--primary)', marginLeft: 8, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>Review Now <ArrowRight size={14} /></Link>
            </div>
          )}
          {stats?.pendingWithdrawals > 0 && (
            <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Banknote size={16} /> <strong>{stats.pendingWithdrawals}</strong> withdrawal{stats.pendingWithdrawals !== 1 ? 's' : ''} pending.
              <Link to="/admin/withdrawals" style={{ color: 'var(--primary)', marginLeft: 8, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>Process Now <ArrowRight size={14} /></Link>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}